/**
 * YipYip — <yy-split>
 *
 * Wraps each word of its text in a span carrying an index, so CSS can stagger
 * them. Words, not characters: character splitting bloats the DOM and turns
 * the heading into gibberish for screen readers.
 *
 * The original sentence is preserved on an aria-label and the wrapped words
 * are hidden from the accessibility tree, so assistive tech reads one clean
 * string either way.
 *
 * Under prefers-reduced-motion this does nothing at all — the text stays as
 * authored.
 */
class YipSplit extends HTMLElement {
  connectedCallback() {
    if (this.dataset.split === 'done') return;

    // Respect the user's motion preference by simply not splitting.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.dataset.split = 'done';
      return;
    }

    const text = this.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;

    const words = text.split(' ');

    // Bail on anything long enough that per-word spans stop being worth it.
    if (words.length > 40) {
      this.dataset.split = 'done';
      return;
    }

    this.setAttribute('aria-label', text);

    const frag = document.createDocumentFragment();
    words.forEach((word, i) => {
      const span = document.createElement('span');
      span.className = 'yy-word';
      span.style.setProperty('--i', i);
      span.setAttribute('aria-hidden', 'true');
      span.textContent = word;
      frag.appendChild(span);
      if (i < words.length - 1) {
        frag.appendChild(document.createTextNode(' '));
      }
    });

    this.textContent = '';
    this.appendChild(frag);
    this.dataset.split = 'done';
  }
}

if (!customElements.get('yy-split')) {
  customElements.define('yy-split', YipSplit);
}

/**
 * Publishes the real header height as --yy-header-h so sticky offsets and
 * scroll-margin-top can both reference it. The header condenses on scroll, so
 * a hardcoded value would be wrong half the time.
 */
(function trackHeaderHeight() {
  const setHeight = () => {
    const header = document.querySelector('.section-header, .header-wrapper');
    if (!header) return;
    const h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) {
      document.documentElement.style.setProperty('--yy-header-h', `${h}px`);
    }
  };

  const start = () => {
    setHeight();
    if ('ResizeObserver' in window) {
      const header = document.querySelector('.section-header, .header-wrapper');
      if (header) new ResizeObserver(setHeight).observe(header);
    } else {
      window.addEventListener('resize', setHeight, { passive: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', setHeight);
  document.addEventListener('shopify:section:unload', setHeight);
})();
